import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import { updateEventParticipantStatus } from 'app/frontend/base/api/event'
import { CharacterClassIcon } from 'app/frontend/base/components'
import { useContrastedBackground } from 'app/frontend/base/components/Card'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import EventStatusIcon from 'app/frontend/base/components/EventStatusIcon'
import { Link } from 'react-router-dom'
import { useState } from 'react'
import {
  EventOverviewStatusByEventValue,
  useUserStat,
  type UserStatView,
} from '../utils/useUserStat'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { eventParticipantStatuses } from 'app/frontend/types/custom/EventParticipantStatus'
import { useTeam } from 'app/frontend/base/config/context/team'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { displayErrorToast } from 'app/frontend/base/helpers'
import EventParticipantStatusItems from '../../TeamEvent/components/EventParticipantStatusItems'
import { classColours } from 'app/frontend/types/custom/CharacterClass'

export type EventOverviewUserProps = {
  main: wowaudit.TeamMembership
  members: wowaudit.TeamMembership[]
  visibleEvents: wowaudit.EventReference[]
  statusByEvent: Map<number, EventOverviewStatusByEventValue>
  statView: UserStatView
}

const EventOverviewUser = (props: EventOverviewUserProps) => {
  const { team } = useTeam()
  const { main, visibleEvents, statusByEvent } = props
  const {
    data: { user },
  } = usePageInfo()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const [openDropdownEventId, setOpenDropdownEventId] = useState<number>()

  const background = useContrastedBackground()
  const { tooltipValue, displayValue } = useUserStat(props)
  const { accessLevel } = useAuth()

  const isOwnCharacter = user && user?.id === main.characterReference.userId
  const editable = accessLevel === 'admin' || isOwnCharacter
  const selectableStatuses = eventParticipantStatuses.filter(
    (nextStatus) =>
      nextStatus !== 'removed' &&
      (accessLevel === 'admin' ||
        team.settings.raidsAvailableStatuses.value.includes(nextStatus)),
  )
  const { mutate: updateStatus, isPending: isUpdatingStatus } = useMutation({
    mutationFn: ({
      eventParticipantId,
      nextStatus,
    }: {
      eventParticipantId: number
      nextStatus: wowaudit.EventParticipantReference['status']
    }) => updateEventParticipantStatus(team.id, eventParticipantId, nextStatus),
    onSuccess: (updatedEvent) => {
      queryClient.setQueryData(
        ['teamEvents', { id: team.id }],
        (existingEvents: wowaudit.EventReference[] | undefined) => {
          if (!existingEvents) return existingEvents
          return existingEvents.map((event) =>
            event.id === updatedEvent.id ? updatedEvent : event,
          )
        },
      )

      toast.success(t('dashboard.team.events.status-updated'))
      setOpenDropdownEventId(undefined)
    },
    onError: (error) => {
      displayErrorToast(error, t)
    },
  })

  return (
    <tr
      className={cn(
        '[&>td]:border-y [&>td]:py-1 [&>td:last-child]:border-r [&>td:last-child]:rounded-r-md',
        background,
      )}
    >
      <td className={cn('border-l rounded-l-md pl-1')}>
        <div
          className={cn(
            'flex items-center gap-2',
            classColours[main.characterReference.characterClass].text,
          )}
        >
          <CharacterRoleIcon role={main.role} />
          <CharacterClassIcon
            size={'medium'}
            characterClass={main.characterReference.characterClass}
          />
          <Link to={main.characterReference.path} className='min-w-0'>
            {main.characterReference.name}
          </Link>
        </div>
      </td>

      <td className='px-2'>
        <Tooltip>
          <TooltipContent>{tooltipValue}</TooltipContent>
          <TooltipTrigger className='w-full text-xs font-normal text-right'>
            {displayValue}
          </TooltipTrigger>
        </Tooltip>
      </td>

      {visibleEvents.map((event) => {
        const eventStatus = statusByEvent.get(event.id)
        const status = eventStatus?.status ?? 'unknown'
        const participantId = eventStatus?.participantId
        const canEditParticipant = editable && participantId !== undefined
        return (
          <td
            key={event.id}
            className={cn(
              'border-l relative',
              canEditParticipant && 'hover:bg-card transition-colors',
              event.optional ? 'bg-info' : '',
              event.status === 'cancelled' ? 'bg-destructive' : '',
            )}
          >
            {canEditParticipant ? (
              <DropdownMenu
                open={openDropdownEventId === event.id}
                onOpenChange={(open) =>
                  setOpenDropdownEventId(open ? event.id : undefined)
                }
              >
                <DropdownMenuTrigger
                  className='focus:outline-none focus:bg-card absolute inset-0 flex items-center justify-center'
                  disabled={isUpdatingStatus}
                >
                  <EventStatusIcon status={status} size='small' />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className='w-fit p-1'
                  side='bottom'
                  align='center'
                >
                  <EventParticipantStatusItems
                    currentStatus={status}
                    statuses={selectableStatuses}
                    onSelect={(nextStatus) =>
                      updateStatus({
                        eventParticipantId: participantId,
                        nextStatus,
                      })
                    }
                  />
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className='flex justify-center'>
                <EventStatusIcon status={status} size='small' />
              </div>
            )}
          </td>
        )
      })}
    </tr>
  )
}

export default EventOverviewUser
